import moment from 'moment';
import { Item } from 'shared-types';
import { ExtraSelectionGroupType, SelectionOrExtraType } from 'shared-types/Basket';
import { TranslationsFunc } from '@lang';
import type { BasketCardExtra, BasketCardItem } from 'voyager';
import { Routes } from '@common/constants';
import { formatExtraLabel } from '@common/helpers/formatExtraLabel';
import { PackageNames, packageNamesByRoomId, spaDayTitle } from '@common/helpers/packageName';
import { firstSpaChoiceTime, spaChoiceLines } from '@common/helpers/spaChoices';
import { FulfilableBasketItem } from '@features/AddedToBasket/Fulfilable/FulfilableAddedToBasket.types';
import { GOLF_DEFAULT_COURSE_IMAGE_URL } from '@features/golf/assets';
import { bookedRestaurantSegment, bookingPath } from '@features/tables/bookingRoute';
import {
  golfCourseId,
  golfHireLines,
  golfPerPlayerMinorUnits,
  golfPlayerCount,
  golfPlayerNames,
  golfTeeTime,
} from '@features/golf/basketLine';
import { isNoValueTableBooking } from '@utils/basket/noValueTableBooking';
import { pluralizeAdults, pluralizeChildren, pluralizeGuests } from '../../BasketDrawer.helpers';

export const ROOM_EXTRA_PREFIX = 'room-extra:';
export const SPA_GUEST_EXTRA_PREFIX = 'spa-guest-extra:';
export const SPA_SELECTION_EXTRA_PREFIX = 'spa-selection-extra:';

export type ToBasketCardItemDeps = {
  t: TranslationsFunc;
  currencyCode: string;
  moduleImages?: {
    rooms?: string;
    spa?: string;
    tables?: string;
    golf?: string;
  };
  hideArrivalTimes?: boolean;
  packageNames?: PackageNames;
};

const buildHref = (
  route: Routes,
  params: Record<string, string | undefined>
): string | undefined => {
  let path: string = route;
  for (const [key, value] of Object.entries(params)) {
    if (value == null) return undefined;
    path = path.replace(`[${key}]`, encodeURIComponent(String(value)));
  }
  return path;
};

type HrefFulfilable = Pick<FulfilableBasketItem, 'type' | 'eventId' | 'voucherId'>;

const isFulfilable = (item: Item | HrefFulfilable): item is HrefFulfilable =>
  'type' in item && (item.type === 'event' || item.type === 'voucher');

const formatDate = (date?: string) => (date ? moment(date).format('ddd, D MMM') : undefined);

const formatDateRange = (checkIn?: string, checkOut?: string) => {
  if (!checkIn) return undefined;
  const start = formatDate(checkIn);
  if (!checkOut) return start;
  return `${start} – ${formatDate(checkOut)}`;
};

const formatMomentDateTime = (value: moment.Moment) =>
  `${value.format('ddd, D MMM')} · ${value.format('HH:mm')}`;

const formatDateTime = (date?: string) => (date ? formatMomentDateTime(moment(date)) : undefined);

const formatArrival = (
  date: string | undefined,
  hideArrivalTimes?: boolean,
  startTime?: string
) => {
  if (hideArrivalTimes) return formatDate(date);
  return startTime && date ? `${formatDate(date)} · ${startTime}` : formatDateTime(date);
};

const formatVisit = (
  checkIn?: string,
  checkOut?: string,
  hideArrivalTimes?: boolean,
  startTime?: string
) =>
  checkIn && checkOut && moment(checkIn).isSame(moment(checkOut), 'day')
    ? formatArrival(checkIn, hideArrivalTimes, startTime)
    : formatDateRange(checkIn, checkOut);

const guestCountLine = (
  adults: number | undefined,
  children: number | undefined,
  t: TranslationsFunc
) => {
  const parts: string[] = [];
  if (adults && adults > 0) parts.push(`${adults} ${pluralizeAdults(adults, t)}`);
  if (children && children > 0) parts.push(`${children} ${pluralizeChildren(children, t)}`);
  return parts.length > 0 ? parts.join(', ') : undefined;
};

const firstImageUrl = (item: Item): string | undefined =>
  item.bookable?.imageUrl ?? item.image?.url ?? item.imageGallery?.[0]?.url ?? undefined;

const moduleImageFor = (
  type: string | undefined,
  moduleImages: ToBasketCardItemDeps['moduleImages']
): string | undefined => {
  if (!type || !moduleImages) return undefined;
  if (type === 'rooms') return moduleImages.rooms;
  if (type === 'tables') return moduleImages.tables;
  if (type === 'golf') return moduleImages.golf;
  if (type === 'spa-packages' || type.startsWith('spa')) return moduleImages.spa;
  return undefined;
};

const compactMeta = (...lines: Array<string | undefined>): string[] =>
  lines.filter((line): line is string => Boolean(line));

const isPackageItem = (item: Item) =>
  item.dynamicPackage === 'parent' || item.dynamicPackage === 'child';

const isPackageChild = (item: Item) => item.dynamicPackage === 'child';

const isPackageParent = (item: Item) => item.dynamicPackage === 'parent';

const packageRemoveConfirmation = (
  item: Item,
  t: TranslationsFunc
): NonNullable<BasketCardItem['removeConfirmation']> => {
  const packageName = item.bookable?.rate;
  return {
    title: t('basketDrawer.removePackage.title'),
    message: packageName
      ? t('basketDrawer.removePackage.message', { packageName })
      : t('basketDrawer.removePackage.messageNoName'),
    confirmLabel: t('basketDrawer.removePackage.confirm'),
    cancelLabel: t('basketDrawer.removePackage.cancel'),
    closeLabel: t('basketDrawer.removePackage.close'),
  };
};

const guestNames = (item: Item): string[] =>
  (Array.isArray(item.guestDetails) ? item.guestDetails : [])
    .map((guest) => `${guest?.firstName ?? ''} ${guest?.lastName ?? ''}`.trim())
    .filter((name) => name.length > 0);

const spaGuestLine = (item: Item, guestCount: number, t: TranslationsFunc) => {
  const names = isPackageChild(item) ? guestNames(item) : [];
  if (names.length > 0) return names.join(', ');
  return guestCount > 0 ? `${guestCount} ${pluralizeGuests(guestCount, t)}` : undefined;
};

const labelPackageExtras = (
  item: Item,
  extras: BasketCardExtra[],
  labelKey: 'basketDrawer.packageRoomExtra' | 'basketDrawer.packageSpaExtra',
  t: TranslationsFunc
): BasketCardExtra[] =>
  isPackageItem(item)
    ? extras.map((extra) => ({ ...extra, label: t(labelKey, { extra: extra.label }) }))
    : extras;

const roomToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const meta = compactMeta(
    item.bookable?.rate,
    formatDateRange(item.checkIn, item.checkOut),
    guestCountLine(item.adults, item.children, deps.t)
  );
  const roomExtras = item.bookable?.roomExtras;
  const extras: BasketCardExtra[] = (Array.isArray(roomExtras) ? roomExtras : [])
    .map((extra, index) => ({
      key: extra.id != null ? `${ROOM_EXTRA_PREFIX}${extra.id}` : `room-extra-${index}`,
      label: formatExtraLabel(extra.name, extra.quantity),
      removable: extra.id != null,
    }))
    .filter(({ label }) => !!label);
  const labelledExtras = labelPackageExtras(item, extras, 'basketDrawer.packageRoomExtra', deps.t);
  return {
    id: String(item.id),
    title: item.bookable?.name ?? item.name,
    href: basketItemHref(item),
    imageUrl: firstImageUrl(item) ?? moduleImageFor('rooms', deps.moduleImages),
    meta,
    extras: labelledExtras.length > 0 ? labelledExtras : undefined,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const spaRouteForType = (type: string | undefined): Routes | undefined => {
  switch (type) {
    case 'spa-treatments':
      return Routes.SPA_TREATMENT_DETAIL;
    case 'spa-days':
      return Routes.SPA_DAY_DETAIL;
    case 'spa-facilities':
      return Routes.FACILITY_DETAIL;
    default:
      return undefined;
  }
};

type SpaExtraLike = {
  id?: number | string;
  name?: string;
  extra?: { id?: number | string; name?: string };
};

const groupSpaExtras = (extras: SpaExtraLike[], keyPrefix: string): BasketCardExtra[] => {
  const grouped = extras.reduce<
    Record<string, { name: string; count: number; hasSourceId: boolean }>
  >((acc, extra) => {
    const sourceId = extra?.id ?? extra?.extra?.id;
    const id = String(sourceId ?? extra?.name ?? '');
    const name = extra?.name ?? extra?.extra?.name;
    if (!id || !name) return acc;
    if (!acc[id]) acc[id] = { name, count: 0, hasSourceId: sourceId != null };
    acc[id].count += 1;
    return acc;
  }, {});

  return Object.entries(grouped).map(([id, { name, count, hasSourceId }]) => ({
    key: `${keyPrefix}${id}`,
    label: formatExtraLabel(name, count),
    removable: hasSourceId,
  }));
};

const groupSelectionExtras = (selections: SpaExtraLike[]): BasketCardExtra[] => {
  const grouped = selections.reduce<
    Record<string, { name: string; count: number; extraId?: string }>
  >((acc, selection) => {
    const extraId = selection?.extra?.id;
    const name = selection?.extra?.name ?? selection?.name;
    const id = String(extraId ?? selection?.id ?? name ?? '');
    if (!id || !name) return acc;
    if (!acc[id]) {
      acc[id] = { name, count: 0, extraId: extraId != null ? String(extraId) : undefined };
    }
    acc[id].count += 1;
    return acc;
  }, {});

  return Object.entries(grouped).map(([id, { name, count, extraId }]) => ({
    key: `${SPA_SELECTION_EXTRA_PREFIX}${id}`,
    label: formatExtraLabel(name, count),
    removable: extraId != null,
  }));
};

const collectSpaExtras = (item: Item): BasketCardExtra[] => {
  const guestDetails = Array.isArray(item.guestDetails) ? item.guestDetails : [];
  const guestExtras: SpaExtraLike[] = guestDetails.flatMap((guest) =>
    Array.isArray(guest?.extras) ? guest.extras : []
  );

  const selectionGroups: SelectionOrExtraType[] = Array.isArray(item.selectionGroups)
    ? (item.selectionGroups as SelectionOrExtraType[])
    : [];
  const selectionGroupExtras: SpaExtraLike[] = selectionGroups
    .filter((group): group is ExtraSelectionGroupType => !!group?.isExtras)
    .flatMap(
      (group) =>
        (Array.isArray(group.selections) ? group.selections : []) as unknown as SpaExtraLike[]
    );

  return [
    ...groupSpaExtras(guestExtras, SPA_GUEST_EXTRA_PREFIX),
    ...groupSelectionExtras(selectionGroupExtras),
  ];
};

const spaToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const guestCount = (item.guestDetails ?? []).length;
  const meta = compactMeta(
    formatArrival(item.checkIn, deps.hideArrivalTimes),
    spaGuestLine(item, guestCount, deps.t)
  );

  const extras = labelPackageExtras(
    item,
    collectSpaExtras(item),
    'basketDrawer.packageSpaExtra',
    deps.t
  );

  return {
    id: String(item.id),
    title: item.bookable?.name ?? item.name,
    href: basketItemHref(item),
    imageUrl: firstImageUrl(item) ?? moduleImageFor(item.type, deps.moduleImages),
    meta,
    extras: extras.length > 0 ? extras : undefined,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const diningToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const meta = compactMeta(
    formatDateTime(item.checkIn),
    guestCountLine(item.adults, item.children, deps.t)
  );
  return {
    id: String(item.id),
    title: item.bookable?.ids?.restaurantName ?? item.bookable?.name ?? item.name,
    href: basketItemHref(item),
    imageUrl: firstImageUrl(item) ?? moduleImageFor('tables', deps.moduleImages),
    meta,
    price: isNoValueTableBooking(item)
      ? undefined
      : { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const formatGolfTeeTime = (item: Item) => {
  const teeTime = golfTeeTime(item);

  return teeTime ? formatMomentDateTime(teeTime) : undefined;
};

const golfToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const players = golfPlayerCount(item);

  const meta = compactMeta(
    formatGolfTeeTime(item),
    players > 0 ? deps.t('golf.playerCount', { count: players }) : undefined
  );

  const playerNames: BasketCardExtra[] = golfPlayerNames(item).map((name, index) => ({
    key: `player-${index}`,
    label: name,
  }));
  const hireLines: BasketCardExtra[] = golfHireLines(item).map((hire) => ({
    key: `golf-hire-${hire.id}`,
    label: formatExtraLabel(hire.name, hire.quantity),
    removable: false,
  }));
  const extras = [...playerNames, ...hireLines];
  const perPlayer = golfPerPlayerMinorUnits(item);

  return {
    id: String(item.id),
    title: item.bookable?.name ?? item.name,
    href: basketItemHref(item),
    imageUrl:
      firstImageUrl(item) ??
      moduleImageFor('golf', deps.moduleImages) ??
      GOLF_DEFAULT_COURSE_IMAGE_URL,
    meta,
    extras: extras.length > 0 ? extras : undefined,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
    subPrice: perPlayer ? { amount: perPlayer, currencyCode: deps.currencyCode } : undefined,
    subPriceSuffix: perPlayer ? deps.t('golf.perPlayer') : undefined,
  };
};

const productToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const quantity = item.quantity ?? 1;
  const meta = compactMeta(
    item.option?.name,
    `${deps.t('basketDrawer.productListing.quantity')}: ${quantity}`
  );
  return {
    id: String(item.id),
    title: item.product?.name ?? item.name,
    href: basketItemHref(item),
    imageUrl: item.product?.imageGallery?.[0]?.url ?? firstImageUrl(item),
    meta,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const spaPackageToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const guestCount = item.guestDetails?.length ?? item.numberOfGuests ?? 0;
  const { title, spaDayLine } = spaDayTitle(
    item,
    deps.packageNames,
    item.spaPackage?.name ?? item.bookable?.name ?? item.name
  );
  const meta = compactMeta(
    spaDayLine,
    formatVisit(
      item.checkIn,
      item.checkOut,
      deps.hideArrivalTimes,
      isPackageChild(item) ? firstSpaChoiceTime(item) : undefined
    ),
    spaGuestLine(item, guestCount, deps.t),
    ...spaChoiceLines(item)
  );
  const extras = labelPackageExtras(
    item,
    collectSpaExtras(item),
    'basketDrawer.packageSpaExtra',
    deps.t
  );
  return {
    id: String(item.id),
    title,
    href: basketItemHref(item),
    imageUrl:
      item.spaPackage?.image?.url ??
      firstImageUrl(item) ??
      moduleImageFor('spa-packages', deps.moduleImages),
    meta,
    extras: extras.length > 0 ? extras : undefined,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const fulfilableHref = (item: HrefFulfilable): string | undefined => {
  if (item.type === 'event') {
    const eventId = item.eventId;
    return buildHref(Routes.EVENT_DETAIL, {
      id: eventId != null ? String(eventId) : undefined,
    });
  }
  if (item.type === 'voucher') {
    const voucherId = item.voucherId;
    return buildHref(Routes.VOUCHER_DETAIL, {
      id: voucherId != null ? String(voucherId) : undefined,
    });
  }
  return undefined;
};

const idParam = (id: unknown): string | undefined => (id != null ? String(id) : undefined);

export const basketItemHref = (item: Item | HrefFulfilable): string | undefined => {
  if (isFulfilable(item)) return fulfilableHref(item);
  switch (item.type) {
    case 'rooms':
      return buildHref(Routes.ROOM_DETAIL, {
        id: idParam(item.bookable?.ids?.roomId ?? item.bookable?.id),
      });
    case 'tables': {
      const segment = bookedRestaurantSegment(item.bookable?.ids);
      return segment ? bookingPath(segment) : Routes.TABLES_HOME;
    }
    case 'golf':
      return buildHref(Routes.GOLF_PLAY_COURSE, { course: golfCourseId(item) });
    case 'products':
      return buildHref(Routes.PRODUCT_DETAIL, { id: idParam(item.product?.id) });
    case 'spa-packages':
      return buildHref(Routes.SPA_DAY_DETAIL, {
        id: idParam(item.spaPackage?.id ?? item.bookable?.id),
      });
    default: {
      const route = spaRouteForType(item.type);
      return route
        ? buildHref(route, { id: idParam(item.bookable?.ids?.id ?? item.bookable?.id) })
        : undefined;
    }
  }
};

const fulfilableToBasketCardItem = (
  item: FulfilableBasketItem,
  deps: ToBasketCardItemDeps
): BasketCardItem => {
  const quantity = item.quantity ?? 1;
  const meta = compactMeta(
    item.eventId && item.detailsBag ? undefined : item.briefDescription,
    quantity > 1 ? `${deps.t('basketDrawer.productListing.quantity')}: ${quantity}` : undefined
  );
  // detailsBag prices are in minor units (priceAsInt); item.price is a number major units fallback
  const amount = item.detailsBag?.priceAsInt ? item.detailsBag.priceAsInt / 100 : item.price ?? 0;
  return {
    id: String(item.id),
    title: item.detailsBag?.name ?? item.name,
    href: basketItemHref(item),
    imageUrl: item.detailsBag?.images?.[0] ?? item.mainImageUrl,
    meta,
    price: { amount, currencyCode: deps.currencyCode },
  };
};

const itemToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  switch (item.type) {
    case 'rooms':
      return roomToBasketCardItem(item, deps);
    case 'tables':
      return diningToBasketCardItem(item, deps);
    case 'golf':
      return golfToBasketCardItem(item, deps);
    case 'products':
      return productToBasketCardItem(item, deps);
    case 'spa-packages':
      return spaPackageToBasketCardItem(item, deps);
    default:
      // Spa entities have varying types (spa-treatments, spa-classes, spa-days, etc.)
      if (item.type?.startsWith('spa')) return spaToBasketCardItem(item, deps);
      // Conservative fallback: render the item with its raw name + price.
      return {
        id: String(item.id),
        title: item.bookable?.name ?? item.name,
        imageUrl: firstImageUrl(item) ?? moduleImageFor(item.type, deps.moduleImages),
        price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
      };
  }
};

export const toBasketCardItem = (
  item: Item | FulfilableBasketItem,
  deps: ToBasketCardItemDeps
): BasketCardItem => {
  if (isFulfilable(item)) return fulfilableToBasketCardItem(item, deps);
  const card = itemToBasketCardItem(item, deps);
  if (isPackageChild(item)) {
    return { ...card, removable: false, price: card.price?.amount ? card.price : undefined };
  }
  if (isPackageParent(item)) {
    return { ...card, removeConfirmation: packageRemoveConfirmation(item, deps.t) };
  }
  return card;
};

export const toBasketCardItems = (
  items: Array<Item | FulfilableBasketItem>,
  deps: ToBasketCardItemDeps
): BasketCardItem[] => {
  const packageNames = packageNamesByRoomId(items);
  return items.map((item) => toBasketCardItem(item, { ...deps, packageNames }));
};
